import { useContext, useEffect, useState, useCallback } from "react";
import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { useResizeDetector } from "react-resize-detector";
import DashboardService from "../../../../services/DashboardService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact";

const statusColors = {
  "In Progress": "#4BB6FF",
  Closed: "#EA580C",
  Pending: "#157ABE",
};

const QuizzesLineChart = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  const [rawData, setRawData] = useState([]);
  const [quizData, setQuizData] = useState([["Quiz"]]); 
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const shortenName = useCallback((name, maxLength = 100) => {
    if (!name) return "";
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + "...";
  }, []);
const formatChartData = useCallback(
  (data, status) => {
    const keyMap = {
      "In Progress": "inProgress",
      Closed: "closed",
      Pending: "pending",
    };
    const allStatuses = ["In Progress", "Closed", "Pending"];


    const filteredData = data.filter((item) => {
      return allStatuses.some((s) => (item[keyMap[s]] || 0) > 0);
    });

    if (filteredData.length === 0) {
      return [["Quiz"], ["No data", 0]];
    }

    if (status !== "All") {
      const statusFilteredData = filteredData.filter(
        (item) => (item[keyMap[status]] || 0) > 0
      );
      
      if (statusFilteredData.length === 0) {
        return [["Quiz"], ["No data", 0]];
      }

      return [
        ["Quiz", status],
        ...statusFilteredData.map((item) => [
          shortenName(item.quizName || ""),
          item[keyMap[status]],
        ]),
      ];
    }

    const activeStatuses = allStatuses.filter((statusName) =>
      filteredData.some((item) => (item[keyMap[statusName]] || 0) > 0)
    );

    if (activeStatuses.length === 0) {
      return [["Quiz"], ["No data", 0]];
    }

    const header = ["Quiz", ...activeStatuses];
    const rows = filteredData.map((item) => {
      const row = [shortenName(item.quizName || "")];
      activeStatuses.forEach((s) => {
        const value = item[keyMap[s]] || 0;
        row.push(value > 0 ? value : null);
      });
      return row;
    });

    return [header, ...rows];
  },
  [shortenName]
);

 const getSummaryLabel = (count) => {
  switch (selectedStatus) {
    case "Pending":
      return count === 1 ? "Pending User" : "Pending Users";

    case "In Progress":
      return count === 1 ? "User In Progress" : "Users In Progress";

    case "Closed":
      return count === 1 ? "Completed User" : "Completed Users";

    default:
      return count === 1 ? "Total Quiz User" : "Total Quiz Users";
  }
};

useEffect(() => {
    const controller = new AbortController();

    const fetchQuizData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await DashboardService.GetQuizChart(
          authContext,
          controller.signal
        );

        if (controller.signal.aborted) return;

        if (!response) return;
        if (
          response &&
          response.isSuccess &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          setRawData(response.data);
        } else {
          setError(
            "Failed to fetch quizzes: " +
              (response?.message || "No data received")
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        setError("Failed to fetch quizzes: " + err.message);
        console.error("Quiz API error:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchQuizData();

    return () => controller.abort();
  }, [authContext]);


  useEffect(() => {
    if (rawData.length > 0) {
      setQuizData(formatChartData(rawData, selectedStatus));
    } else {
      setQuizData([["Quiz"], ["No data", 0]]);
    }
  }, [rawData, selectedStatus, formatChartData]);

  // Dynamic colors 
  const getActiveColors = useCallback(() => {
    if (selectedStatus !== "All") {
      return [statusColors[selectedStatus]];
    }

    const keyMap = {
      "In Progress": "inProgress",
      Closed: "closed",
      Pending: "pending",
    };

    const activeStatuses = ["In Progress", "Closed", "Pending"].filter((s) =>
      rawData.some((item) => (item[keyMap[s]] || 0) > 0)
    );

    return activeStatuses.map((s) => statusColors[s]);
  }, [rawData, selectedStatus]);

  const getMaxYValue = useCallback(() => {
    if (quizData.length <= 1) return 100;
    const values = quizData
      .slice(1)
      .flatMap((row) => row.slice(1).map(Number));
    const max = Math.max(...values, 0);
    return Math.ceil(max * 1.2) || 100;
  }, [quizData]);

  const totalQuizzes =
    quizData.length > 1
      ? quizData
          .slice(1)
          .reduce(
            (sum, row) => sum + row.slice(1).reduce((a, v) => a + Number(v), 0),
            0
          )
      : 0;

const options = {
  backgroundColor: "transparent",
  chartArea: {
    width: "80%",
    height: "70%",
    bottom: quizData.length > 4 ? 80 : 60, // Adjust bottom spacing
    top: 20,
  },
  legend: {
    position: totalQuizzes === 0 ? "none" : "bottom",
    textStyle: {
      color: isDark ? "#eaeaea" : "#333",
      fontSize: 11,
      fontName: "Poppins",
    },
  },
  hAxis: {
    gridlines: { count: 4, color: isDark ? "#444" : "#eaeaea" },
    textStyle: { color: isDark ? "#eaeaea" : "#444", fontName: "Poppins" },
    slantedText: quizData.length > 4, 
    slantedTextAngle: 65,
    showTextEvery: 1,
    textPosition: "out",
  },
  vAxis: {
    minValue: 0,
    viewWindow: { min: 0 },
    maxValue: getMaxYValue(),
    gridlines: { count: 5, color: isDark ? "#444" : "#eaeaea" },
    textStyle: { color: isDark ? "#eaeaea" : "#444", fontName: "Poppins" },
  },
  tooltip: {
    isHtml: false,
    trigger: "focus",
    textStyle: { fontSize: 10, fontName: "Poppins" },
    showColorCode: true,
  },
  colors: getActiveColors(),
  curveType: "function",
  pointSize: 5,
};
  const { width, ref } = useResizeDetector();

  if (loading) {
    return (
      <div className="charts" style={{ height: "357px", padding: "14px", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton width="120px" height="24px" />
          <Skeleton width="100px" height="24px" />
        </div>
        <Skeleton width="180px" height="15px" className="mt-2" />
        <Skeleton width="100%" height="250px" className="mt-4" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: "357px", padding: 14, color: "red", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        {error}
      </div>
    );
  }

  return (
    <div
      className="charts p-3"
      style={{
        height: "357px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      ref={ref}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 16}}>Quiz</div>
          <p style={{ fontSize: 13, color: isDark ? "#eaeaea" : "#6c757d", margin: "4px 0 0" }}>
      {getSummaryLabel(totalQuizzes)}: <b>{totalQuizzes}</b>
          </p>
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{
            fontSize: 12,
            border: "1px solid #ddd",
            borderRadius: 6,
            padding: "6px 10px",
            backgroundColor: isDark ? "#222" : "#fff",
            color: isDark ? "#eaeaea" : "#000",
            cursor: "pointer",
          }}
        >
          <option value="All">All</option>
          <option value="In Progress">In Progress</option>
          <option value="Pending">Pending</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {totalQuizzes === 0 ? (
          <div
            style={{
              color: isDark ? "#999" : "#888",
              fontSize: 15,
              textAlign: "center",
            }}
          >
            No quiz data available
          </div>
        ) : width ? (
          <Chart
            key={width}
            chartType="LineChart"
            data={quizData}
            options={options}
            width={`${width - 50}px`}
            height="265px"
          />
        ) : null}
      </div>
    </div>
  );
};

export default QuizzesLineChart;
